<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2.5 px-2.5 py-2 rounded-lg min-w-0 transition-colors"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-n-slate-12 bg-n-solid-3 dark:bg-n-solid-3': isActive && !hasActiveChild,
      'text-n-slate-12 bg-n-solid-3 dark:bg-n-solid-3': hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center">
      <Icon v-if="icon" :icon="icon" class="size-[18px]" />
      <span
        v-if="showBadge"
        class="size-2 -top-0.5 ltr:-right-0.5 rtl:-left-0.5 bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-2 flex-grow min-w-0">
      <span class="text-[13px] font-medium leading-5 truncate">
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="rounded-full text-xs leading-4 font-medium text-center px-1.5 py-0.5 flex-shrink-0"
        :class="{
          'text-n-slate-12 bg-n-alpha-4': isActive || hasActiveChild,
          'text-n-slate-11 bg-n-alpha-3': !isActive && !hasActiveChild,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3.5 text-n-slate-10"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
